import React, { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { MobileNavigationProvider } from "./context/MobileNavigationContext";
import { applyThemePreference, getStoredThemePreference } from "./utils/theme";

import AccountsLayout from "./components/AccountsLayout";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";

import AccountsDashboard from "./pages/AccountsDashboard";
import AccountsComplaints from "./pages/AccountsComplaints";
import AccountsLogin from "./pages/AccountsLogin";
import AccountsMonitor from "./pages/AccountsMonitor";
import GstExportReports from "./pages/GstExportReports";
import GeneralServices from "./pages/GeneralServices";
import AccountsProfile from "./pages/AccountsProfile";
import AccountsVouchers from "./pages/AccountsVouchers";
import AdminAccounts from "./pages/AdminAccounts";
import AdminLogin from "./pages/AdminLogin";
import AdminProfile from "./pages/AdminProfile";
import AdminDailyUpdates from "./pages/AdminDailyUpdates";
import AdminTravel from "./pages/AdminTravel";
import AdminVouchers from "./pages/AdminVouchers";
import WebsiteEnquiries from "./pages/WebsiteEnquiries";
import AccountingNotes from "./pages/AccountingNotes";
import AbbWarrantyComplaints from "./pages/AbbWarrantyComplaints";
import Complaints from "./pages/Complaints";
import CreateComplaint from "./pages/CreateComplaint";
import CreateCustomer from "./pages/CreateCustomer";
import ClientOnboarding from "./pages/ClientOnboarding";
import CreateReport from "./pages/CreateReport";
import CurrentComplaints from "./pages/CurrentComplaints";
import CreateDealer from "./pages/CreateDealer";
import CustomerDetails from "./pages/CustomerDetails";
import Customers from "./pages/Customers";
import CustomerGroups from "./pages/CustomerGroups";
import CustomerGroupProfile from "./pages/CustomerGroupProfile";
import CustomerGroupQuotations from "./pages/CustomerGroupQuotations";
import DailyUpdate from "./pages/DailyUpdate";
import Dashboard from "./pages/Dashboard";
import DashboardAlerts from "./pages/DashboardAlerts";
import DealerDetails from "./pages/DealerDetails";
import Dealers from "./pages/Dealers";
import DeliveryChallans from "./pages/DeliveryChallans";
import EditCustomer from "./pages/EditCustomer";
import EngineerDashboard from "./pages/EngineerDashboard";
import EngineerLogin from "./pages/EngineerLogin";
import EngineerProfile from "./pages/EngineerProfile";
import Engineers from "./pages/Engineers";
import EarthPit from "./pages/EarthPit";
import MyComplaints from "./pages/MyComplaints";
import MyReports from "./pages/MyReports";
import MaterialStock from "./pages/MaterialStock";
import ProductMapping from "./pages/ProductMapping";
import PdfReportPage from "./pages/PdfReportPage";
import ProjectExpenses from "./pages/ProjectExpenses";
import PurchaseInvoices from "./pages/PurchaseInvoices";
import PurchaseOrders from "./pages/PurchaseOrders";
import Quotations from "./pages/Quotations";
import ReportView from "./pages/ReportView";
import RoleSelect from "./pages/RoleSelect";
import SalesInvoices from "./pages/SalesInvoices";
import PerformaInvoices from "./pages/PerformaInvoices";
import SerialDetails from "./pages/SerialDetails";
import TravelUpdate from "./pages/TravelUpdate";
import TestCertificateSettings from "./pages/TestCertificateSettings";
import Vouchers from "./pages/Vouchers";
import OperationsHub from "./pages/OperationsHub";
import SalesPurchaseHub from "./pages/SalesPurchaseHub";
import FinanceHub from "./pages/FinanceHub";
import ContactsHub from "./pages/ContactsHub";
import AdministrationHub from "./pages/AdministrationHub";
import CompanyProfile from "./pages/CompanyProfile";
import AdminUsers from "./pages/AdminUsers";
import RolesPermissions from "./pages/RolesPermissions";
import ActivityLogs from "./pages/ActivityLogs";
import RecycleBin from "./pages/RecycleBin";
import BackupRestore from "./pages/BackupRestore";
import SystemSettings from "./pages/SystemSettings";
import Reports from "./pages/Reports";
import AttendanceRoleSelect from "./pages/AttendanceRoleSelect";
import AttendanceLogin from "./pages/AttendanceLogin";
import AttendancePortal from "./pages/AttendancePortal";

function App() {
  useEffect(() => {
    const syncTheme = () => {
      applyThemePreference(getStoredThemePreference());
    };

    syncTheme();

    if (!window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (getStoredThemePreference() === "system") {
        syncTheme();
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return (
    <MobileNavigationProvider>
      <Router>
        <Routes>
          {/* ── Public routes ── */}
          <Route path="/" element={<RoleSelect />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/engineer-login" element={<EngineerLogin />} />
          <Route path="/accounts-login" element={<AccountsLogin />} />
          <Route path="/attendance" element={<AttendanceRoleSelect />} />
          <Route path="/attendance/login/:role" element={<AttendanceLogin />} />
          <Route path="/serial/:serialNo" element={<SerialDetails />} />
          <Route path="/portal/onboarding" element={<ClientOnboarding />} />
          <Route path="/onboarding/:token" element={<ClientOnboarding />} />

          {/* ════════════════════════════════════════════
              ADMIN routes — role="admin" required
          ════════════════════════════════════════════ */}
          <Route element={<ProtectedRoute allowedRole="admin" />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin/dashboard-alerts" element={<DashboardAlerts />} />
              <Route path="/operations" element={<OperationsHub />} />
              <Route path="/sales-purchase" element={<SalesPurchaseHub />} />
              <Route path="/finance" element={<FinanceHub />} />
              <Route path="/contacts" element={<ContactsHub />} />
              <Route path="/admin/administration" element={<AdministrationHub />} />
              <Route path="/admin/travel" element={<AdminTravel />} />
              <Route path="/admin/daily-updates" element={<AdminDailyUpdates />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customer-groups" element={<CustomerGroups />} />
              <Route path="/customer-groups/:id" element={<CustomerGroupProfile />} />
              <Route path="/customer-groups/:id/quotations" element={<CustomerGroupQuotations />} />
              <Route path="/admin/abb-warranty-complaints" element={<AbbWarrantyComplaints />} />
              <Route path="/abb-warranty-complaints" element={<AbbWarrantyComplaints />} />
              <Route path="/admin/dealers" element={<Dealers />} />
              <Route path="/admin/dealers/create" element={<CreateDealer />} />
              <Route path="/admin/dealers/:id/edit" element={<CreateDealer />} />
              <Route path="/admin/dealers/:id" element={<DealerDetails />} />
              <Route path="/create-customer" element={<CreateCustomer />} />
              <Route path="/edit-customer/:id" element={<EditCustomer />} />
              <Route path="/customer/:id" element={<CustomerDetails />} />
              <Route path="/engineers" element={<Engineers />} />
              <Route path="/admin/accounts" element={<AdminAccounts />} />
              <Route path="/complaints" element={<Complaints />} />
              <Route path="/create-complaint" element={<CreateComplaint />} />
              <Route path="/complaints/:id/edit" element={<CreateComplaint />} />
              <Route path="/current-complaints" element={<CurrentComplaints />} />
              <Route path="/general-services" element={<GeneralServices />} />
              <Route path="/admin/project-expenses" element={<ProjectExpenses />} />
              <Route path="/admin/project-expenses/create" element={<Navigate to="/admin/project-expenses" replace />} />
              <Route path="/reports" element={<Navigate to="/complaints" replace />} />
              <Route path="/reports/create" element={<Navigate to="/complaints" replace />} />
              <Route path="/reports/merge/create" element={<CreateReport />} />
              <Route path="/reports/:id/edit" element={<CreateReport />} />
              <Route path="/reports/:id" element={<ReportView />} />
              <Route path="/reports/:id/pdf" element={<PdfReportPage />} />
              <Route path="/admin/material-stock" element={<MaterialStock />} />
              <Route path="/admin/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/admin/purchase-invoices" element={<PurchaseInvoices />} />
              <Route path="/admin/quotations" element={<Quotations />} />
              <Route path="/admin/sales-invoices" element={<SalesInvoices />} />
              <Route path="/admin/performa-invoices" element={<PerformaInvoices />} />
              <Route path="/admin/delivery-challans" element={<DeliveryChallans />} />
              <Route path="/admin/vouchers" element={<AdminVouchers />} />
              <Route path="/admin/website-enquiries" element={<WebsiteEnquiries />} />
              <Route path="/admin/profile" element={<AdminProfile />} />
              <Route path="/admin/reports-list" element={<Reports />} />
              <Route path="/admin/company-profile" element={<CompanyProfile />} />
              <Route path="/admin/user-management/admins" element={<AdminUsers />} />
              <Route path="/admin/roles-permissions" element={<RolesPermissions />} />
              <Route path="/admin/activity-logs" element={<ActivityLogs />} />
              <Route path="/admin/recycle-bin" element={<RecycleBin />} />
              <Route path="/admin/backup-restore" element={<BackupRestore />} />
              <Route path="/admin/system-settings" element={<SystemSettings />} />
              <Route path="/admin/product-mapping" element={<ProductMapping />} />
              <Route path="/admin/accounts-monitor" element={<AccountsMonitor />} />
              <Route path="/admin/gst-export-reports" element={<GstExportReports />} />
              <Route path="/admin/debit-credit-notes" element={<AccountingNotes />} />
              <Route path="/admin/test-certificate-settings" element={<TestCertificateSettings />} />
            </Route>
          </Route>

          {/* ════════════════════════════════════════════
              ACCOUNTS routes — role="accounts" required
          ════════════════════════════════════════════ */}
          <Route element={<ProtectedRoute allowedRole="accounts" />}>
            <Route element={<AccountsLayout />}>
              <Route path="/accounts-dashboard" element={<AccountsDashboard />} />
              <Route path="/accounts/dashboard-alerts" element={<DashboardAlerts />} />
              <Route path="/accounts/dealers" element={<Dealers />} />
              <Route path="/accounts/dealers/create" element={<CreateDealer />} />
              <Route path="/accounts/dealers/:id/edit" element={<CreateDealer />} />
              <Route path="/accounts/dealers/:id" element={<DealerDetails />} />
              <Route path="/accounts/material-stock" element={<MaterialStock />} />
              <Route path="/accounts/product-mapping" element={<ProductMapping />} />
              <Route path="/accounts/purchase-invoices" element={<PurchaseInvoices />} />
              <Route path="/accounts/sales-invoices" element={<SalesInvoices />} />
              <Route path="/accounts/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/accounts/material-stock/purchase" element={<PurchaseInvoices />} />
              <Route path="/accounts/material-stock/sale" element={<SalesInvoices />} />
              <Route path="/accounts/material-stock/order" element={<PurchaseOrders />} />
              <Route path="/accounts/quotations" element={<Quotations />} />
              <Route path="/accounts/delivery-challans" element={<DeliveryChallans />} />
              <Route path="/accounts/performa-invoices" element={<PerformaInvoices />} />
              <Route path="/accounts/vouchers" element={<AccountsVouchers />} />
              <Route path="/accounts/accounts-monitor" element={<AccountsMonitor />} />
              <Route path="/accounts/gst-export-reports" element={<GstExportReports />} />
              <Route path="/accounts/debit-credit-notes" element={<AccountingNotes />} />
              <Route path="/accounts/material-stock/new" element={<MaterialStock />} />
              <Route path="/accounts/customers" element={<Customers />} />
              <Route path="/accounts/customer-groups" element={<CustomerGroups />} />
              <Route path="/accounts/abb-warranty-complaints" element={<AbbWarrantyComplaints />} />
              <Route path="/accounts/customer-groups/:id" element={<CustomerGroupProfile />} />
              <Route path="/accounts/customer-groups/:id/quotations" element={<CustomerGroupQuotations />} />
              <Route path="/accounts/create-customer" element={<CreateCustomer />} />
              <Route path="/accounts/edit-customer/:id" element={<EditCustomer />} />
              <Route path="/accounts/customer/:id" element={<CustomerDetails />} />
              <Route path="/accounts/complaints" element={<AccountsComplaints />} />
              <Route path="/accounts/create-complaint" element={<CreateComplaint />} />
              <Route path="/accounts/complaints/:id/edit" element={<CreateComplaint />} />
              <Route path="/accounts/profile" element={<AccountsProfile />} />
              <Route path="/accounts/reports/:id" element={<ReportView />} />
              <Route path="/accounts/reports/:id/pdf" element={<PdfReportPage />} />
              <Route path="/accounts/project-expenses" element={<ProjectExpenses />} />
              <Route path="/accounts/project-expenses/create" element={<Navigate to="/accounts/project-expenses" replace />} />
            </Route>
          </Route>

          {/* ════════════════════════════════════════════
              ENGINEER routes — role="engineer" required
          ════════════════════════════════════════════ */}
          <Route element={<ProtectedRoute allowedRole="engineer" />}>
              <Route path="/engineer-dashboard" element={<EngineerDashboard />} />
              <Route path="/engineer-complaints" element={<MyComplaints />} />
              <Route path="/engineer/general-services" element={<GeneralServices />} />
              <Route path="/engineer/customers" element={<Customers />} />
              <Route path="/engineer/customer-groups" element={<CustomerGroups />} />
              <Route path="/engineer/customer-groups/:id" element={<CustomerGroupProfile />} />
              <Route path="/engineer/customer-groups/:id/quotations" element={<CustomerGroupQuotations />} />
              <Route path="/engineer/customer/:id" element={<CustomerDetails />} />
              <Route path="/my-reports" element={<MyReports />} />
              <Route path="/my-reports/:id" element={<ReportView />} />
              <Route path="/my-reports/:id/pdf" element={<PdfReportPage />} />
              <Route path="/engineer/create-complaint" element={<CreateComplaint />} />
              <Route path="/engineer/earth-pit" element={<EarthPit />} />
              <Route path="/engineer/travel" element={<TravelUpdate />} />
              <Route path="/engineer/vouchers" element={<Vouchers />} />
              <Route path="/engineer/daily-update" element={<DailyUpdate />} />
              <Route path="/engineer/profile" element={<EngineerProfile />} />
              <Route path="/create-report" element={<CreateReport />} />
              <Route path="/create-report/:id" element={<CreateReport />} />
          </Route>

          <Route element={<ProtectedRoute allowedRole="engineer" />}>
            <Route path="/attendance/engineer" element={<AttendancePortal role="engineer" />} />
          </Route>
          <Route element={<ProtectedRoute allowedRole="lg_admin" />}>
            <Route path="/attendance/lg-admin" element={<AttendancePortal role="lg_admin" />} />
          </Route>
          <Route element={<ProtectedRoute allowedRole="admin" />}>
            <Route path="/attendance/admin" element={<AttendancePortal role="admin" />} />
          </Route>
        </Routes>
      </Router>
    </MobileNavigationProvider>
  );
}

export default App;
